import React, {useEffect, useState} from 'react';
import MainLayout from '../components/MainLayout';
import Server from '../components/Server';
import {useLocation, useNavigate } from 'react-router-dom';
import OrderReviewSidebar from '../components/OrderReviewSidebar';
import OrderDetails from '../components/OrderDetails';
import useCart from '../hooks/useCart';
import GuardedNavigationModal from '../components/modals/GuardedNavigationModal';
import useNavigationGuard from '../hooks/useNavigationGuard';
import MainLayoutWithoutFooter from '../components/MainLayoutWithoutFooter';
import useEditCartItem from '../hooks/useEditCartItem';
import EditModal from '../components/modals/EditModal';
import { getActiveServerId } from '../utils/staffSession';

const OrderReview = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const checkoutType = location.state?.checkout_type || null
    const editOrderId = location.state?.edit_order_id || null;

    const orderType = location.state?.orderType || localStorage.getItem('orderType');
    // 0, "0" and "" all mean "no server was recorded" - 205,130 takeaway orders in the estate are
    // stored with server_id = 0 rather than NULL. Normalised once here because the two differ in
    // JS: 0 is falsy but "0" is truthy, and whether this arrives as an int or a string depends on
    // the connection's emulatePrepares setting, not on anything this screen controls.
    const orderServerId = Number(location.state?.order_server_id) > 0
        ? location.state.order_server_id
        : null;

    let selectedTables = {};
    try {
        selectedTables = location.state?.selectedTables || JSON.parse(localStorage.getItem('selectedTables') || '{}');
    } catch {
        selectedTables = {};
    }
    const existing_order_id = location.state?.existing_order_id || null;
    const selected_customer_id = location.state?.selected_customer_id || null;
    // location.state.selected_table_ids is never actually set by any navigate() call into this
    // page - TakeawayCart.js only ever passes `selectedTables`, which is shaped inconsistently
    // by the time it gets here (a flat [id] array from a single tap, a {tabId: [ids]} object from
    // multi-select) - so without this fallback, the backend's registerOrderBeforePayment() never
    // receives table ids and silently never creates the table_occupancy row for this order,
    // regardless of table type. Object.values().flat() happens to normalize both shapes to the
    // same flat id list either way.
    const selected_table_ids = location.state?.selected_table_ids
        || (Object.values(selectedTables).flat().length > 0
            ? Object.values(selectedTables).flat().join(',')
            : null);
    const initialServiceType = location.state?.service_type || null;
    // selectedTables' shape can't reliably say which zone this order belongs to (see above) -
    // tabId is passed alongside it by every entry point (useTableOrdering.js -> Takeaway.js ->
    // TakeawayCart.js) specifically to answer that, with the same localStorage fallback the rest
    // of this page's nav-state reads already use for a reload losing location.state.
    const tabId = location.state?.tabId || localStorage.getItem('selectedTabId') || null;
    const isPagerOrder = tabId === 'pager-tab';
    // A PIN-identified staff member is the server, so the picker is redundant for them. The
    // dine-in path already works this way (TakeawayCart::handleQuickSendToKitchen short-circuits
    // and never opens this screen); express/takeaway/delivery comes through here and did not.
    //
    // Still shown when an existing order carries its own server - reviewing someone else's order
    // should keep, and allow changing, the server it was taken by.
    const activeStaffServerId = getActiveServerId();
    const isAutoTaggedServer = !orderServerId && !!activeStaffServerId;
    const [selectedServerId, setSelectedServerId] = useState(() => orderServerId || activeStaffServerId || null);


    const [refresh, setRefresh] = useState(false);
    const {
        cartItems,
        dealsItem,
        halfAndHalfItems,
        servers,
        handleUpdateQty,
        handleRemoveItem,
        netTotal,
        removeID
      } = useCart(refresh,editOrderId);
    const cartItemsExist = Array.isArray(cartItems) && cartItems.length > 0;
    const { showModal, confirmNavigation, cancelNavigation, guardedNavigate } = useNavigationGuard(orderType && cartItemsExist);
    const handleConfirm = () => {
        localStorage.removeItem('orderType');
        localStorage.removeItem('selectedTables');
        confirmNavigation();
      };
   
      useEffect(() => {
        if (orderServerId) {
          setSelectedServerId(orderServerId);
        }
      }, [orderServerId]);
      
    useEffect(() => {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            navigate('/');
        }
    }, [navigate]);

    useEffect(() => {
        if (!orderType) {
          navigate('/dashboard');
        }
      }, [orderType, navigate]);
      
    // useCart reloads the cart itself after a quantity change or a removal; toggling `refresh` here as
    // well made it fetch the whole cart a second time, one request after the other.
    const onUpdateQty = async (cartId, newQty) => {
        await handleUpdateQty(cartId, newQty);
      };
      
      const onRemoveItem = async (cartId) => {
        await handleRemoveItem(cartId);
      };

      const {
        selectedItem,
        isModalOpen,
        openEditModal,
        closeModal
      } = useEditCartItem(editOrderId);

    return (
        <MainLayoutWithoutFooter  guardedNavigate={guardedNavigate}>
            <div className="pageContent orederReviewPage">
                <div className="container">
                    <h2 className="lgTitle mb-3">Cart</h2>
                    {isAutoTaggedServer ? (
                        <div className="cardStyle mb-4">
                            <div className="cardBody">
                                <strong>Server:</strong> {localStorage.getItem('active_staff_name') || 'Current staff'}
                            </div>
                        </div>
                    ) : (
                        <Server
                            servers={servers}
                            selectedServerId={selectedServerId}
                            onChange={setSelectedServerId}
                        />
                    )}
                    <h2 className="lgTitle mb-3">Order Details</h2>
                    <div className="row g-3">
                        <OrderDetails
                            cartItems={cartItems}
                            dealsItem={dealsItem}
                            halfAndHalfItems={halfAndHalfItems}
                            handleUpdateQty={onUpdateQty}
                            handleRemoveItem={onRemoveItem}
                            removeID={removeID} 
                            orderType={orderType}
                            selectedTables={selectedTables}
                            onEditCartItem={openEditModal}
                            existingOrderId={existing_order_id}
                            />
                            
                        <OrderReviewSidebar netTotal={netTotal}   
                        orderType={orderType}
                        selectedTables={selectedTables}   
                        existing_order_id={existing_order_id}
                        selected_customer_id={selected_customer_id}
                        selected_table_ids={selected_table_ids}
                        serverId={selectedServerId}
                        servers={servers}
                        editOrderId={editOrderId}
                        initialServiceType={initialServiceType}
                        isPagerOrder={isPagerOrder}
                        />
                    </div>
                </div>
            </div>
            {isModalOpen && (
  <EditModal
    item={selectedItem}
    isOpen={isModalOpen}
    onClose={closeModal}
    onCartChange={() => setRefresh(prev => !prev)}
    editOrderId={editOrderId}
  />
)}

            <GuardedNavigationModal 
            isOpen={showModal} 
            onCancel={cancelNavigation} 
            onConfirm={handleConfirm} 
        />
        </MainLayoutWithoutFooter>
    );
};

export default OrderReview;